import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProjectService } from '../services/ProjectService';
import { Play, Pause, Save, RotateCcw, Check, X, Scissors, Type, Download, Share2, Youtube, XCircle, Package, Heart, RefreshCw, Loader2, Crop } from 'lucide-react';
import { CaptionPickerModal } from '../components/CaptionPickerModal';
import { ClipTrimmerModal } from '../components/ClipTrimmerModal';
import { CleanupModal } from '../components/CleanupModal';
import SmartClipPlayer from '../components/SmartClipPlayer';
import { ImportConfigPanel } from '../components/ImportConfigPanel';
import { ProcessingCard } from '../components/ProcessingCard';
import { CAPTION_PRESETS } from '../lib/CaptionPresets';
import logo from '../assets/logo.png';
import './ClipIntelligencePage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Default caption config
const DEFAULT_CAPTION_CONFIG = {
  styleId: 'youshaei',
  size: 80,
  verticalPosition: 20,
  saveAsDefault: true
};

// Load saved defaults from localStorage
const loadCaptionDefaults = () => {
  try {
    const saved = localStorage.getItem('clip_intel_caption_defaults');
    if (saved) {
      return { ...DEFAULT_CAPTION_CONFIG, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.error('Failed to load caption defaults:', e);
  }
  return DEFAULT_CAPTION_CONFIG;
};

// Timeline trimmer component for each clip
const ClipTrimmer = ({ start, end, originalStart, originalEnd, duration, onChange, disabled }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(null);

  // Calculate the visible window (allow extending 5s before/after original bounds, within video duration)
  const windowStart = Math.max(0, originalStart - 5);
  const windowEnd = Math.min(duration, originalEnd + 5);
  const windowDuration = windowEnd - windowStart;

  const handlePointerDown = (type) => (e) => {
    if (disabled) return;
    setIsDragging(type);
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!isDragging || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const percentage = x / rect.width;
      const time = windowStart + (percentage * windowDuration);
      const snappedTime = Math.round(time * 10) / 10;

      if (isDragging === 'start') {
        const newStart = Math.min(snappedTime, end - 0.5);
        onChange('start', Math.max(windowStart, newStart));
      } else if (isDragging === 'end') {
        const newEnd = Math.max(snappedTime, start + 0.5);
        onChange('end', Math.min(windowEnd, newEnd));
      }
    };

    const handleUp = () => setIsDragging(null);

    if (isDragging) {
      window.addEventListener('pointermove', handleMove, { passive: false });
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    }
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [isDragging, start, end, windowStart, windowDuration, onChange]);

  const leftPct = ((start - windowStart) / windowDuration) * 100;
  const rightPct = ((end - windowStart) / windowDuration) * 100;
  const widthPct = rightPct - leftPct;

  return (
    <div className="clip-trimmer">
      <div className="trimmer-header">
        <span className="trimmer-label">Trim</span>
        <div className="trimmer-times">
          <span className="time-badge">{start.toFixed(1)}s</span>
          <span className="time-separator">→</span>
          <span className="time-badge">{end.toFixed(1)}s</span>
          <span className="time-duration">({(end - start).toFixed(1)}s)</span>
        </div>
      </div>
      <div className="trimmer-track" ref={containerRef}>
        <div className="trimmer-bg"></div>
        <div
          className="trimmer-selection"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        >
          <div
            className={`trimmer-handle handle-left ${disabled ? 'disabled' : ''}`}
            onPointerDown={handlePointerDown('start')}
          >
            <div className="handle-grip"></div>
          </div>
          <div
            className={`trimmer-handle handle-right ${disabled ? 'disabled' : ''}`}
            onPointerDown={handlePointerDown('end')}
          >
            <div className="handle-grip"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Individual clip card with editing controls
const ClipCard = ({ clip, rendered, isApproved, jobId, videoDuration, session, onApprove, onDelete, onRerender, projectData, transcript, savedCaptionConfig }) => {
  const navigate = useNavigate();
  
  // Use saved caption settings from job if available, otherwise use defaults
  const defaultCaptionSize = savedCaptionConfig?.size || 90;
  const defaultCaptionStyle = savedCaptionConfig?.style_id || 'default';
  const defaultCaptionVerticalPosition = savedCaptionConfig?.vertical_position || 20;
  
  const [clipState, setClipState] = useState({
    start: clip.start,
    end: clip.end,
    includeCaptions: true,
    captionSize: defaultCaptionSize,
    captionStyle: defaultCaptionStyle,
    captionVerticalPosition: defaultCaptionVerticalPosition,
  });
  
  // Sync caption settings when savedCaptionConfig loads (may arrive after initial render)
  useEffect(() => {
    console.log('[ClipCard] savedCaptionConfig:', savedCaptionConfig);
    if (savedCaptionConfig) {
      console.log('[ClipCard] Applying saved caption config:', {
        size: savedCaptionConfig.size,
        style_id: savedCaptionConfig.style_id,
        vertical_position: savedCaptionConfig.vertical_position,
      });
      setClipState(prev => ({
        ...prev,
        captionSize: savedCaptionConfig.size || prev.captionSize,
        captionStyle: savedCaptionConfig.style_id || prev.captionStyle,
        captionVerticalPosition: savedCaptionConfig.vertical_position || prev.captionVerticalPosition,
      }));
    }
  }, [savedCaptionConfig]);
  
  const [showStylePicker, setShowStylePicker] = useState(false);
  const [showTrimmer, setShowTrimmer] = useState(false);

  const [rerendering, setRerendering] = useState(false);
  const [smartCropping, setSmartCropping] = useState(false);
  const [error, setError] = useState('');
  const [videoKey, setVideoKey] = useState(0); // Force video reload after re-render

  const hasChanges = (
    clipState.start !== clip.start ||
    clipState.end !== clip.end ||
    !clipState.includeCaptions ||
    clipState.captionSize !== defaultCaptionSize ||
    clipState.captionStyle !== defaultCaptionStyle
  );

  // Custom JIT Export Logic
  const [exporting, setExporting] = useState(false);
  const [exportTarget, setExportTarget] = useState(null); // 'youtube' | 'download'

  const handleExport = async (target) => { // target: 'youtube' | 'download'
    setExporting(true);
    setExportTarget(target);

    try {
      const clipTitle = (clip.title || 'clip').replace(/[^a-zA-Z0-9 ]/g, '').trim().substring(0, 50);

      // For both download and YouTube, we need to ensure clip is rendered first
      // But we DON'T want to change videoKey (which would remount the player)
      if (hasChanges || !rendered) {
        // Render the clip without updating the video player
        await handleRenderForExport();
      }

      if (target === 'download') {
        const downloadUrl = `${API_BASE}/api/clip-intel/jobs/${jobId}/clips/${clip.clip_id}/download?filename=${encodeURIComponent(clipTitle)}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${clipTitle}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (target === 'youtube') {
        const previewUrl = `/api/clip-intel/jobs/${jobId}/clips/${clip.clip_id}.mp4`;
        const fullPreviewUrl = `${API_BASE}${previewUrl}`;
        
        const projectData = {
          id: `clip_${jobId}_${clip.clip_id}`,
          name: `Clip: ${clipTitle}`,
          created_at: new Date().toISOString(),
          output_url: fullPreviewUrl,
          data: {
            script: clip.transcript || '',
            video_url: fullPreviewUrl,
            supabase_path: `clips/${session.user.id}/${jobId}/${clip.clip_id}.mp4`
          }
        };
        navigate('/youtube', { state: { preselect: projectData } });
      }

    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
      setExportTarget(null);
    }
  };

  // Render clip for export - creates the file on backend WITHOUT touching UI state
  const handleRenderForExport = async () => {
    if (!session?.access_token) {
      throw new Error('Please log in');
    }
    
    let targetJobId = jobId;
    if (!targetJobId && clip.clip_id) {
      const parts = clip.clip_id.split('_');
      targetJobId = parts.length > 2 ? parts[1] : parts[0];
    }

    // Trigger the render
    const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${targetJobId}/clips/${clip.clip_id}/rerender`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        start: clipState.start,
        end: clipState.end,
        include_captions: clipState.includeCaptions,
        caption_size: clipState.captionSize,
        caption_style: clipState.captionStyle,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Render failed');

    // Poll for completion - just wait for file to be ready, DON'T update any UI state
    // Render can take 2-3 minutes: Modal cold start + download + ffmpeg + upload
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 120;  // 120 × 1.5s = 3 minutes
      const pollInterval = 1500;

      const pollForCompletion = async () => {
        attempts++;
        try {
          const checkRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${targetJobId}/results?_t=${Date.now()}`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            const updatedClip = (checkData.rendered || checkData.clips || []).find(c => c.clip_id === clip.clip_id);
            
            if (updatedClip?.cloud_path || updatedClip?.preview_url) {
              // Clip file is ready for download - DON'T call onRerender, just resolve
              // Updating parent state causes all clips to re-render and break
              resolve();
              return;
            }
          }
        } catch (e) {
          console.error('[Export] Polling error:', e);
        }

        if (attempts < maxAttempts) {
          setTimeout(pollForCompletion, pollInterval);
        } else {
          reject(new Error('Render timed out'));
        }
      };

      pollForCompletion();
    });
  };

  const handleTrimChange = (field, value) => {
    setClipState(prev => ({ ...prev, [field]: value }));
  };

  const handleCaptionToggle = () => {
    setClipState(prev => ({ ...prev, includeCaptions: !prev.includeCaptions }));
  };

  const handleCaptionSizeChange = (e) => {
    setClipState(prev => ({ ...prev, captionSize: parseInt(e.target.value, 10) }));
  };

  const handleStyleSelect = (styleId) => {
    setClipState(prev => ({ ...prev, captionStyle: styleId }));
  };

  const handleRerender = async () => {
    if (!session?.access_token) {
      setError('Please log in');
      return;
    }
    setRerendering(true);
    setError('');
    try {
      // Use prop jobId if available, otherwise try to extract.
      // Format 1 (Legacy): ci_<job_id>_<hash> -> index 1
      // Format 2 (New): <job_id>_<index> -> index 0
      let targetJobId = jobId;
      if (!targetJobId && clip.clip_id) {
        const parts = clip.clip_id.split('_');
        targetJobId = parts.length > 2 ? parts[1] : parts[0];
      }

      // First, get the current clip state so we can detect when it changes
      let originalLastUpdated = null;
      try {
        const currentRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${targetJobId}/results`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (currentRes.ok) {
          const currentData = await currentRes.json();
          const currentClip = (currentData.rendered || currentData.clips || []).find(c => c.clip_id === clip.clip_id);
          originalLastUpdated = currentClip?.last_updated || 0;
        }
      } catch (e) {
        // Ignore - we'll poll anyway
      }

      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${targetJobId}/clips/${clip.clip_id}/rerender`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          start: clipState.start,
          end: clipState.end,
          include_captions: clipState.includeCaptions,
          caption_size: clipState.captionSize,
          caption_style: clipState.captionStyle,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Re-render failed');

      // Return a promise that resolves when polling is done
      return new Promise((resolve) => {
        // Render started successfully - now poll for completion
        // Render can take 2-3 minutes: Modal cold start + download + ffmpeg + upload
        let attempts = 0;
        const maxAttempts = 120;  // 120 × 1.5s = 3 minutes
        const pollInterval = 1500;

        console.log(`[ClipIntel] Starting polling for clip ${clip.clip_id}, originalLastUpdated=${originalLastUpdated}`);

        const pollForCompletion = async () => {
          attempts++;
          try {
            // Check if the clip's last_updated timestamp has changed (indicating re-render is complete)
            const checkRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${targetJobId}/results?_t=${Date.now()}`, {
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (checkRes.ok) {
              const checkData = await checkRes.json();
              const updatedClip = (checkData.rendered || checkData.clips || []).find(c => c.clip_id === clip.clip_id);
              const newLastUpdated = updatedClip?.last_updated || 0;

              console.log(`[ClipIntel] Poll #${attempts}: newLastUpdated=${newLastUpdated}`);

              // Detect if the clip was actually updated (last_updated timestamp is newer)
              if (newLastUpdated > originalLastUpdated) {
                console.log('[ClipIntel] Change detected! Refreshing video.');
                // Clip has been updated! Refresh the video
                const newKey = Date.now();
                setVideoKey(newKey);
                const newUrl = `/api/clip-intel/jobs/${targetJobId}/clips/${clip.clip_id}.mp4?t=${newKey}`;
                onRerender(clip.clip_id, { preview_url: newUrl });
                setRerendering(false);
                resolve(newUrl); // Resolve the promise
                return;
              }
            }
          } catch (e) {
            console.error('[ClipIntel] Polling error:', e);
          }

          if (attempts < maxAttempts) {
            setTimeout(pollForCompletion, pollInterval);
          } else {
            console.warn('[ClipIntel] Polling timed out');
            // Timeout - refresh anyway and hope for the best
            const newKey = Date.now();
            setVideoKey(newKey);
            const fallbackUrl = `/api/clip-intel/jobs/${targetJobId}/clips/${clip.clip_id}.mp4?t=${newKey}`;
            onRerender(clip.clip_id, { preview_url: fallbackUrl });
            setRerendering(false);
            resolve(fallbackUrl); // Resolve anyway
          }
        };

        // Start polling almost immediately (1s delay to allow network propagation)
        setTimeout(pollForCompletion, 1000);
      });

    } catch (e) {
      setError(e.message);
      setRerendering(false);
      throw e; // Re-throw failure
    }
  };

  const handleReset = () => {
    setClipState({
      start: clip.start,
      end: clip.end,
      includeCaptions: true,
      captionSize: defaultCaptionSize,
      captionStyle: defaultCaptionStyle,
      captionVerticalPosition: defaultCaptionVerticalPosition,
    });
  };

  const handleSmartCrop = async () => {
    if (!session?.access_token) {
      setError('Please log in');
      return;
    }
    setSmartCropping(true);
    setError('');
    try {
      let targetJobId = jobId;
      if (!targetJobId && clip.clip_id) {
        const parts = clip.clip_id.split('_');
        targetJobId = parts.length > 2 ? parts[1] : parts[0];
      }

      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${targetJobId}/clips/${clip.clip_id}/smart-crop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          include_captions: clipState.includeCaptions,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Smart crop failed');

      // Update rendered list to show the new clip
      if (onRerender && data.preview_url) {
        onRerender(clip.clip_id, {
          preview_url: data.preview_url,
          cloud_path: `clips/${clip.clip_id}.mp4`, // Mark as rendered
          smart_crop: true
        });
      }

      // Force video element to reload with new rendered URL
      setVideoKey(prev => prev + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setSmartCropping(false);
    }
  };

  // Check if source video is unavailable (upload timed out) and no rendered version exists
  // Note: video_url is the original YouTube URL which doesn't work in HTML5 video
  // source_url is the Supabase signed URL which does work
  const hasWorkingSourceUrl = projectData?.source_url && !projectData?.source_video_unavailable;
  const hasRenderedClip = rendered?.cloud_path || rendered?.preview_url;
  const sourceUnavailable = !hasWorkingSourceUrl && !hasRenderedClip;

  return (
    <div className={`clip-card ${isApproved ? 'clip-approved' : ''}`}>
      {/* Smart JIT Player or Render Prompt */}
      <div className="clip-video-container">
        {sourceUnavailable ? (
          // Source video upload failed, show prompt to render
          <div className="source-unavailable-placeholder" style={{
            aspectRatio: '9/16',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            color: '#888',
            fontSize: '13px',
            padding: '20px',
            textAlign: 'center',
            gap: '12px'
          }}>
            <RefreshCw size={24} style={{ opacity: 0.5 }} />
            <span>Source video unavailable</span>
            <button
              onClick={handleRerender}
              disabled={rerendering}
              style={{
                background: rerendering ? '#333' : '#4f46e5',
                color: '#fff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: rerendering ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {rerendering ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Rendering...
                </>
              ) : (
                <>
                  <Play size={14} />
                  Render Preview
                </>
              )}
            </button>
          </div>
        ) : (
          <SmartClipPlayer
            key={`jit-${videoKey}`}
            sourceUrl={projectData?.source_url}
            renderedUrl={hasChanges ? null : (hasRenderedClip ? `${API_BASE}/api/clip-intel/jobs/${jobId}/clips/${clip.clip_id}.mp4?t=${videoKey}` : null)}
            start={clipState.start}
            end={clipState.end}
            captions={transcript || []}
            includeCaptions={hasChanges || (clipState.includeCaptions && !rendered)}
            captionStyle={clipState.captionStyle}
            captionSize={clipState.captionSize}
            captionVerticalPosition={clipState.captionVerticalPosition}
          />
        )}

        {/* If rendered, we might overlay a "Previewing JIT" vs "View Final" switch? 
              For now, JIT player is the main preview. 
              The rendered video is mostly for verification before download.
              Actually, if rendered exists, we should probably prefer the rendered video 
              if the user hasn't made new changes.
              
              BUT existing logic resets rendered on change.
              So if rendered exists, it matches current state.
              The rendered video has captions burned in (usually).
              The JIT player overlays them.
              
              Let's stick to JIT player for consistency unless the user explicitly wants to check the burn-in.
              Or, show "Preview Mode" vs "Final Result".
          */}

        <div className="clip-duration-badge">
          {Math.round(clipState.end - clipState.start)}s
        </div>

        {/* Status Overlay */}


        {/* Like/Approve Badge (Top Left) */}
        <button
          className={`clip-like-badge ${isApproved ? 'active-heart' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onApprove(clip.clip_id);
          }}
          title={isApproved ? "Unsave" : "Save Clip"}
        >
          <Heart size={16} fill={isApproved ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="clip-info-row">
        {/* Left: Score Badge with Tooltip */}
        <div className="score-badge-wrapper">
          <div className="score-badge">
            {Math.round(clip.score.final_score)}
          </div>
          {clip.score.explanation && (
            <div className="score-tooltip">
              <div className="score-tooltip-title">Why this clip?</div>
              <div className="score-tooltip-text">{clip.score.explanation}</div>
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="clip-actions">
          {/* Edit / Trim */}
          <button
            className="icon-btn"
            onClick={() => setShowTrimmer(true)}
            title="Fine-Tune Start/End"
          >
            <Scissors size={18} />
          </button>

          <button
            className="icon-btn"
            onClick={() => setShowStylePicker(true)}
            title="Caption Options"
          >
            <Type size={18} />
          </button>

          {/* Smart Crop (Face Detection) */}
          <button
            className={`icon-btn ${smartCropping ? 'loading' : ''}`}
            onClick={handleSmartCrop}
            disabled={smartCropping}
            title="Smart Crop (Center on Face)"
          >
            {smartCropping ? <Loader2 size={18} className="spin" /> : <Crop size={18} />}
          </button>

          {/* YouTube Export (Triggers JIT Render if needed) */}
          <button
            className="icon-btn"
            disabled={exporting}
            onClick={() => handleExport('youtube')}
            title="Upload to YouTube Studio"
          >
            {exporting && exportTarget === 'youtube' ? <Loader2 size={18} className="spin" /> : <Youtube size={18} />}
          </button>

          {/* Download (Triggers JIT Render if needed) */}
          <button
            className="icon-btn"
            disabled={exporting}
            onClick={() => handleExport('download')}
            title="Download HD"
          >
            {exporting && exportTarget === 'download' ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
          </button>
        </div>
      </div>

      {/* Export Status Message */}
      {exporting && (
        <div className="clip-status-msg" style={{ fontSize: '0.8rem', color: '#06B6D4', padding: '0 12px 8px' }}>
          Finalizing video processing settings...
        </div>
      )}

      <div className="clip-title-row" title={clip.title}>
        {clip.title}
      </div>

      {/* Modals */}
      <CaptionPickerModal
        isOpen={showStylePicker}
        onClose={() => setShowStylePicker(false)}
        onSelect={handleStyleSelect}
        currentStyleId={clipState.captionStyle}
        includeCaptions={clipState.includeCaptions}
        onToggleCaptions={handleCaptionToggle}
        captionSize={clipState.captionSize}
        onSizeChange={handleCaptionSizeChange}
      />

      {
        showTrimmer && (
          <ClipTrimmerModal
            isOpen={showTrimmer}
            onClose={() => setShowTrimmer(false)}
            clip={{ start: clipState.start, end: clipState.end }}
            videoDuration={videoDuration}
            jobId={jobId || (clip.clip_id.split('_').length > 1 ? clip.clip_id.split('_')[0] : null)}
            session={session}
            onSave={(newStart, newEnd) => {
              setClipState(prev => ({ ...prev, start: newStart, end: newEnd }));
            }}
          />
        )
      }

      {
        hasChanges && (
          <button
            className="btn-primary btn-sm"
            style={{ marginTop: '8px', width: '100%' }}
            onClick={handleRerender}
            disabled={rerendering}
          >
            {rerendering ? <RefreshCw size={14} className="spin" /> : 'Apply Changes'}
          </button>
        )
      }

      {error && <div className="clip-error">{error}</div>}
    </div >
  );
};

export const ClipIntelligencePage = ({ session }) => {
  const fileInputRef = useRef(null);

  // Project State
  const [searchParams] = useSearchParams();
  const [projectId, setProjectId] = useState(searchParams.get('project_id'));
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved
  const [lastSaved, setLastSaved] = useState(null);

  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [results, setResults] = useState(null);
  const [transcript, setTranscript] = useState(null); // Added transcript state
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [approved, setApproved] = useState(() => new Set());
  const [showCleanupModal, setShowCleanupModal] = useState(false);

  // URL mode state
  const [inputMode, setInputMode] = useState('file'); // 'file' or 'url'
  const [urlInput, setUrlInput] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);

  // Import preview state (new flow)
  const [importPreview, setImportPreview] = useState(null); // { url, title, thumbnail, duration }
  const [captionConfig, setCaptionConfig] = useState(loadCaptionDefaults);
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [processingMeta, setProcessingMeta] = useState(null); // { title, thumbnail } during processing

  // Simulate progress for initial upload/URL loading only
  // Backend progress is used during processing (set by polling/restoreSession)
  useEffect(() => {
    let interval;
    // Only simulate progress for upload/URL loading, NOT for processing
    // (processing uses real backend progress from status endpoint)
    const isInitialLoading = uploading || urlLoading;

    if (isInitialLoading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev; // Stall at 90% until done
          const increment = prev < 30 ? 5 : prev < 60 ? 2 : 0.5;
          return prev + increment;
        });
      }, 600);
    } else if (!processing) {
      // Only set to 100% when completely done (not processing)
      setProgress(100);
    }
    return () => clearInterval(interval);
  }, [uploading, urlLoading, processing]);

  // Load project logic
  useEffect(() => {
    if (projectId && session?.access_token) {
      loadProject(projectId);
    } else {
      // If no project ID, check for active session in local storage
      const activeJobId = localStorage.getItem('clip_intel_active_job');
      if (activeJobId && session?.access_token) {
        restoreSession(activeJobId);
      }
    }
  }, [projectId, session]);

  const restoreSession = async (id) => {
    try {
      // 1. Fetch Job Details
      const jobRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!jobRes.ok) {
        if (jobRes.status === 404 || jobRes.status === 403 || jobRes.status === 401) {
          localStorage.removeItem('clip_intel_active_job');
          localStorage.removeItem('clip_intel_processing_meta');
          setJob(null);
          setResults(null);
        }
        return;
      }
      const jobData = await jobRes.json();
      setJob(jobData);

      // 2. Check real-time status from /status endpoint
      let currentStatus = null;
      let currentProgress = 0;
      try {
        const statusRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${id}/status`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          currentStatus = statusData.status;
          currentProgress = statusData.progress || 0;
          console.log(`[restoreSession] Job ${id} status: ${currentStatus}, progress: ${currentProgress}%`);
          
          // Restore progress from backend
          if (currentProgress > 0) {
            setProgress(currentProgress);
          }
        }
      } catch (err) {
        console.error("Failed to fetch job status:", err);
      }

      // 3. Check for Results
      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${id}/results`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();

      if (res.ok && data.candidates && data.candidates.length > 0) {
        // Job complete with results
        setResults(data);
        setProcessing(false);
        localStorage.removeItem('clip_intel_processing_meta');

        // Fetch transcript for JIT captions
        try {
          const tRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${id}/transcript`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (tRes.ok) {
            const tData = await tRes.json();
            setTranscript(tData.transcript?.words || tData.words || []);
          }
        } catch (err) {
          console.error("Failed to load transcript:", err);
        }

      } else if (currentStatus === 'failed') {
        // Job failed
        const errorMsg = data.message || 'Processing failed. The video might be private, members-only, or unavailable.';
        setError(errorMsg);
        setProcessing(false);
        localStorage.removeItem('clip_intel_active_job');
        localStorage.removeItem('clip_intel_processing_meta');

      } else {
        // Check if job is still in a processing state
        // Modal uses various status values during processing
        const processingStates = [
          'processing', 'ingested', 'created', 'downloading', 
          'transcribing', 'analyzing', 'rendering', 'uploading'
        ];
        
        const terminalStates = ['complete', 'failed', 'done', 'error'];
        const isStillProcessing = 
          processingStates.includes(currentStatus) ||
          processingStates.includes(jobData.status) ||
          // Also handle case where results not ready but job exists and isn't done/failed/error
          (!res.ok && currentStatus && !terminalStates.includes(currentStatus));
        
        if (isStillProcessing) {
          // Job still processing - restore polling state
          console.log(`[restoreSession] Job ${id} still processing (status: ${currentStatus}), restoring polling state`);
          setProcessing(true);
          // Restore processing metadata from localStorage
          try {
            const savedMeta = localStorage.getItem('clip_intel_processing_meta');
            if (savedMeta) {
              setProcessingMeta(JSON.parse(savedMeta));
            }
          } catch (err) {
            console.error("Failed to restore processing metadata:", err);
          }
        } else {
          // Edge case: job exists but results not found and status is unclear
          // This could happen if job was abandoned or there was an error
          console.warn(`[restoreSession] Job ${id} in unclear state: status=${currentStatus}, results=${res.status}`);
          // Clear the stale job reference
          localStorage.removeItem('clip_intel_active_job');
          localStorage.removeItem('clip_intel_processing_meta');
          setJob(null);
        }
      }
    } catch (e) {
      console.error("Failed to restore session:", e);
    }
  };

  // Poll for completion if processing and have a job ID (but not results yet)
  const [pollCount, setPollCount] = useState(0);
  const MAX_POLL_COUNT = 200; // ~10 minutes at 3s intervals
  
  useEffect(() => {
    let interval;
    if (processing && job?.job_id && !results) {
      interval = setInterval(async () => {
        try {
          // Safety: stop polling after max attempts
          setPollCount(prev => {
            const newCount = prev + 1;
            if (newCount >= MAX_POLL_COUNT) {
              console.warn(`[Poll] Max poll count (${MAX_POLL_COUNT}) reached for job ${job.job_id}`);
              setError('Processing is taking longer than expected. Please try again or use a shorter video.');
              setProcessing(false);
              localStorage.removeItem('clip_intel_job_id');
              localStorage.removeItem('clip_intel_processing_meta');
              return 0;
            }
            return newCount;
          });
          
          // First, check job status to detect failures
          const statusRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${job.job_id}/status`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });

          if (statusRes.ok) {
            const statusData = await statusRes.json();

            // Update progress from backend
            if (statusData.progress !== undefined && statusData.progress > 0) {
              setProgress(statusData.progress);
            }

            // Check if job failed or errored
            if (statusData.status === 'failed' || statusData.status === 'error') {
              const errorMsg = statusData.message || 'Processing failed. The video might be private, members-only, or unavailable.';
              setError(errorMsg);
              setProcessing(false);
              localStorage.removeItem('clip_intel_job_id');
              localStorage.removeItem('clip_intel_processing_meta');
              return; // Stop polling
            }
          }

          // Then check for results
          const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${job.job_id}/results`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          const data = await res.json();

          if (res.ok && data.candidates && data.candidates.length > 0) {
            console.log('[ClipIntel] Results received, caption_config:', data.caption_config);
            setResults(data);
            if (job?.job_id) loadTranscript(job.job_id); // Fetch transcript when polling succeeds
            setProcessing(false);
            setProcessingMeta(null);
            setPollCount(0);
            // Clear processing metadata from localStorage
            localStorage.removeItem('clip_intel_processing_meta');
          }
        } catch (e) {
          console.error("Polling error:", e);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [processing, job, results, session]);

  const loadProject = async (id) => {
    try {
      const project = await ProjectService.getProject(session.access_token, id);
      if (project && project.data) {
        const data = project.data;
        if (data.job) setJob(data.job);
        if (data.results) setResults(data.results);
        if (data.approved) setApproved(new Set(data.approved));
        if (data.inputMode) setInputMode(data.inputMode);
        // Load transcript for JIT captions if job exists with results
        if (data.job?.job_id && data.results?.candidates?.length > 0) {
          loadTranscript(data.job.job_id);

          // Fetch fresh job data to get a new signed source_url (old URLs expire after 2 hours)
          try {
            const jobRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${data.job.job_id}`, {
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (jobRes.ok) {
              const freshJobData = await jobRes.json();
              // Update results with fresh source_url
              if (freshJobData.source_url) {
                setResults(prev => prev ? { ...prev, source_url: freshJobData.source_url } : prev);
              }
            }
          } catch (err) {
            console.error("Failed to refresh source URL:", err);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load project:", err);
    }
  };

  const handleSaveProject = async (resultsOverride = null, jobOverride = null) => {
    // Prevent event object from being treated as results
    if (resultsOverride && (resultsOverride.nativeEvent || resultsOverride.preventDefault)) {
      resultsOverride = null;
    }

    if (!session?.access_token) {
      alert("Please log in to save projects");
      return;
    }

    const currentJob = jobOverride || job;
    if (!currentJob?.job_id) {
      alert("Nothing to save yet! Start a job first.");
      return;
    }

    const currentResults = resultsOverride || results;

    setSaveStatus('saving');
    try {
      const projectData = {
        job: currentJob,
        results: currentResults,
        approved: Array.from(approved),
        inputMode,
        updatedAt: new Date().toISOString()
      };

      const title = currentResults?.profile_name ? `Intel: ${currentResults.profile_name}` : `Clip Intel ${currentJob.job_id.substring(0, 8)}`;
      // Use first clip thumbnail if available, or logo
      const thumbnail = currentResults?.candidates?.[0]?.thumbnail || null;

      if (projectId) {
        await ProjectService.updateProject(session.access_token, projectId, {
          name: title,
          data: projectData,
          type: 'clip_intel'
        });
        setLastSaved(new Date());
      } else {
        const newProject = await ProjectService.createProject(
          session.access_token,
          'clip_intel',
          title,
          projectData
        );
        setProjectId(newProject.id);
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('project_id', newProject.id);
        window.history.pushState({}, '', newUrl);
        setLastSaved(new Date());
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);

    } catch (err) {
      console.error(err);
      alert("Failed to save project");
      setSaveStatus('idle');
    }
  };

  const onPickFile = (f) => {
    setError('');
    setResults(null);
    setApproved(new Set());
    setFile(f || null);
    setJob(null);
  };

  // Helper to load transcript
  const loadTranscript = async (jobId) => {
    try {
      const tRes = await fetch(`${API_BASE}/api/clip-intel/jobs/${jobId}/transcript`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (tRes.ok) {
        const tData = await tRes.json();
        setTranscript(tData.transcript?.words || tData.words || []);
      }
    } catch (err) {
      console.error("Failed to load transcript:", err);
    }
  };

  const handleProcess = async (jobOverride = null) => {
    let id = job?.job_id;
    let jobObj = job;

    if (jobOverride) {
      if (typeof jobOverride === 'string') {
        id = jobOverride;
      } else {
        id = jobOverride.job_id;
        jobObj = jobOverride;
      }
    }

    if (!id || !session?.access_token) return;

    setProcessing(true);
    setError('');

    let shouldStopProcessing = true;

    try {
      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${id}/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ render: true, include_captions: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Processing failed');

      // Handle "processing" status for Modal jobs (no immediate results)
      if (data.status === 'processing') {
        // Don't set results yet, just ensure project exists and let polling take over
        await handleSaveProject(null, jobObj);
        shouldStopProcessing = false; // Keep spinner going for polling
      } else {
        setResults(data);
        await loadTranscript(id); // Fetch transcript immediately if results are ready
        await handleSaveProject(data, jobObj);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      if (shouldStopProcessing) {
        setProcessing(false);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!session?.access_token) {
      setError('Please log in to use Clip Intelligence');
      return;
    }

    setUploading(true);
    setError('');
    setResults(null);

    try {
      // Import Supabase client
      const { supabase } = await import('../lib/supabaseClient');

      if (!supabase) {
        throw new Error('Supabase client not configured');
      }

      // Generate job ID (8-character hex string like backend)
      const jobId = Array.from(crypto.getRandomValues(new Uint8Array(4)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // Determine file extension
      const fileName = file.name || 'video.mp4';
      const ext = fileName.includes('.') ? '.' + fileName.split('.').pop() : '.mp4';

      // Upload directly to Supabase Storage
      // Path format: {user_id}/clip-intel/{job_id}/source{ext}
      const userId = session.user?.id;
      if (!userId) {
        throw new Error('User ID not found in session');
      }

      const remotePath = `${userId}/clip-intel/${jobId}/source${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('user-content')
        .upload(remotePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Call backend with Supabase path
      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/from-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          supabase_path: remotePath,
          original_filename: fileName,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Processing failed');

      setJob(data.job);
      localStorage.setItem('clip_intel_active_job', data.job.job_id);

      // Auto-process
      await handleProcess(data.job);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlFetch = async () => {
    if (!urlInput.trim()) return;
    if (!session?.access_token) {
      setError('Please log in to use Clip Intelligence');
      return;
    }
    setUrlLoading(true);
    setError('');
    setResults(null);
    setImportPreview(null);

    try {
      // Fetch video metadata only (don't start processing yet)
      const res = await fetch(`${API_BASE}/api/metadata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch video info');

      // Set import preview data - this shows the config panel
      setImportPreview({
        url: urlInput.trim(),
        title: data.title || 'Untitled Video',
        thumbnail: data.thumbnail,
        duration: data.duration || 0,
      });
      setUrlInput('');
    } catch (e) {
      setError(e.message);
    } finally {
      setUrlLoading(false);
    }
  };

  // Start processing after user configures settings
  const handleStartProcessing = async () => {
    if (!importPreview?.url || !session?.access_token) return;

    // Save caption defaults if checked
    if (captionConfig.saveAsDefault) {
      try {
        localStorage.setItem('clip_intel_caption_defaults', JSON.stringify(captionConfig));
      } catch (e) {
        console.error('Failed to save caption defaults:', e);
      }
    }

    setUrlLoading(true);
    setProcessingMeta({ title: importPreview.title, thumbnail: importPreview.thumbnail });
    setError('');

    try {
      const captionPayload = {
        style_id: captionConfig.styleId,
        size: captionConfig.size,
        vertical_position: captionConfig.verticalPosition,
      };
      console.log('[ClipIntel] Sending caption_config:', captionPayload);
      
      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/from-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          url: importPreview.url,
          profile_name: 'default',
          caption_config: captionPayload,
          aspect_ratio: aspectRatio,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Processing failed');

      setJob(data.job);
      localStorage.setItem('clip_intel_active_job', data.job.job_id);
      // Store processing metadata for session restoration
      localStorage.setItem('clip_intel_processing_meta', JSON.stringify({
        title: importPreview.title,
        thumbnail: importPreview.thumbnail
      }));
      setImportPreview(null); // Clear preview, show processing state

      // Auto-save project immediately
      await handleProcess(data.job);
    } catch (e) {
      setError(e.message);
      setProcessingMeta(null);
    } finally {
      setUrlLoading(false);
    }
  };

  const handleCancelImport = () => {
    setImportPreview(null);
    setProcessingMeta(null);
  };

  const handleCancelProcessing = () => {
    // Stop all processing state
    setProcessing(false);
    setUrlLoading(false);
    setUploading(false);
    setProcessingMeta(null);
    setJob(null);
    setProgress(0);
    setPollCount(0);
    
    // Clear localStorage
    localStorage.removeItem('clip_intel_job_id');
    localStorage.removeItem('clip_intel_processing_meta');
    localStorage.removeItem('clip_intel_active_job');
    
    // Show feedback
    setError('Processing cancelled. You can start a new job.');
  };



  const handleDeleteClip = async (clipId) => {
    if (!job?.job_id || !session?.access_token) return;
    try {
      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${job.job_id}/clips/${clipId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');

      setResults((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rendered: (prev.rendered || []).filter((r) => r.clip_id !== clipId),
        };
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleApprove = (clipId) => {
    setApproved((prev) => {
      const next = new Set(prev);
      if (next.has(clipId)) next.delete(clipId);
      else next.add(clipId);
      return next;
    });
  };

  const handleRerender = (clipId, data) => {
    // Update the rendered list with the new preview URL and cloud_path
    setResults((prev) => {
      if (!prev) return prev;
      const existingIdx = prev.rendered.findIndex(r => r.clip_id === clipId);
      const newRendered = [...prev.rendered];
      // Preserve existing data and merge new data
      const existing = existingIdx >= 0 ? newRendered[existingIdx] : {};
      const updated = {
        ...existing,
        clip_id: clipId,
        preview_url: data.preview_url,
        // Mark as rendered (cloud_path signals a rendered clip exists)
        cloud_path: data.cloud_path || `clips/${clipId}.mp4`,
      };
      if (existingIdx >= 0) {
        newRendered[existingIdx] = updated;
      } else {
        newRendered.push(updated);
      }
      return { ...prev, rendered: newRendered };
    });
  };

  const handleDownloadApproved = async () => {
    if (!job?.job_id || approved.size === 0) return;
    if (!session?.access_token) {
      setError('Please log in to download');
      return;
    }
    setDownloading(true);
    setError('');
    try {
      // Build clip info with titles for filename generation
      const clipInfo = Array.from(approved).map(clipId => {
        const candidate = results?.candidates?.find(c => c.clip_id === clipId);
        return {
          clip_id: clipId,
          title: candidate?.title || clipId
        };
      });

      // Step 1: Request the server to prepare the ZIP and get a download URL
      const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${job.job_id}/download-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ clip_ids: Array.from(approved), clip_info: clipInfo }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Download failed');
      }

      // Step 2: Use the download URL directly (bypasses fetch blob CORS issues)
      const a = document.createElement('a');
      a.href = `${API_BASE}${data.download_url}`;
      // Use profile name or "clips" for zip filename
      const zipName = results?.profile_name ? `${results.profile_name}_clips.zip` : `clips_${job.job_id.substring(0, 8)}.zip`;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const confirmCleanup = async () => {
    const isSaved = !!projectId;

    if (!isSaved) {
      try {
        const res = await fetch(`${API_BASE}/api/clip-intel/jobs/${job.job_id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Cleanup failed');
      } catch (e) {
        console.error("Cleanup error:", e);
      }
    }

    setJob(null);
    setResults(null);
    setFile(null);
    setApproved(new Set());
    setProjectId(null);
    localStorage.removeItem('clip_intel_active_job');

    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('project_id');
    window.history.pushState({}, '', newUrl);

    setShowCleanupModal(false);
  };

  const handleSaveAndExit = async () => {
    if (!projectId) {
      try {
        await handleSaveProject();
      } catch (e) {
        return;
      }
    }

    // Manually run cleanup logic (same as saved path)
    setJob(null);
    setResults(null);
    setFile(null);
    setApproved(new Set());
    setProjectId(null);
    localStorage.removeItem('clip_intel_active_job');

    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('project_id');
    window.history.pushState({}, '', newUrl);

    setShowCleanupModal(false);
  };

  const handleFinishAndCleanup = () => {
    if (!job?.job_id) return;
    if (!session?.access_token) {
      setError('Please log in');
      return;
    }
    setShowCleanupModal(true);
  };

  const renderedMap = new Map((results?.rendered || []).map((r) => [r.clip_id, r]));
  const videoDuration = results?.duration || job?.duration || 60;

  return (
    <div className="page-container clipintel-page">
      {/* Hero Header */}
      <div className="hero" style={{ padding: '20px 0 40px', minHeight: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <h1 className="hero-heading" style={{ fontSize: '3.5rem', marginBottom: '0' }}>
          Smart Clips
        </h1>
        <p className="hero-subtitle" style={{ fontSize: '1.2rem', color: '#ccc', marginBottom: '30px', marginTop: '4px' }}>
          Turn long-form content into viral short clips automatically.
        </p>

        {/* Unified Input Row */}
        <div className="hero-input-row" style={{ marginBottom: 0 }}>
          <div className="download-form hero-search">
            <input
              type="text"
              placeholder="Paste a Video Link (YouTube, Instagram, TikTok + 1700+ Sites)"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && urlInput.trim()) handleUrlFetch();
              }}
              disabled={urlLoading || processing}
            />
            <button
              onClick={handleUrlFetch}
              disabled={!urlInput.trim() || urlLoading || processing}
            >
              {urlLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
          <span className="hero-or-separator">Or</span>
          <button
            className="hero-upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp4,.mov,.m4v"
            onChange={(e) => onPickFile(e.target.files?.[0] || null)}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Import Config Panel - shown after URL search before processing */}
      {importPreview && !processing && !results && (
        <div style={{ padding: '0 20px', marginBottom: '30px' }}>
          <ImportConfigPanel
            videoMeta={importPreview}
            captionConfig={captionConfig}
            onConfigChange={setCaptionConfig}
            aspectRatio={aspectRatio}
            onAspectChange={setAspectRatio}
            onProcess={handleStartProcessing}
            onCancel={handleCancelImport}
            isProcessing={urlLoading}
          />
        </div>
      )}

      {/* Processing Card - shown during processing */}
      {processingMeta && (processing || urlLoading) && !results && (
        <div style={{ padding: '0 20px', marginBottom: '30px' }}>
          <ProcessingCard
            thumbnail={processingMeta.thumbnail}
            title={processingMeta.title}
            progress={progress}
            estimatedMinutes={Math.ceil((100 - progress) / 12)} // ~8 min for full process
          />
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <button
              onClick={handleCancelProcessing}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#999',
                padding: '8px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.target.style.borderColor = '#ef4444'; e.target.style.color = '#ef4444'; }}
              onMouseOut={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.2)'; e.target.style.color = '#999'; }}
            >
              Cancel Processing
            </button>
          </div>
        </div>
      )}

      {/* Legacy Progress Bar - only show for file uploads or when no processingMeta */}
      {(uploading || (processing && !processingMeta)) && (
        <div className="clipintel-progress-container" style={{ maxWidth: '800px', margin: '0 auto 30px', padding: '0 20px', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#ccc', fontSize: '0.9rem', fontWeight: 500 }}>
            <span>
              {uploading ? 'Beaming up your video to the mothership...' :
                'Applying our AI Magic...'}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div style={{ height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, background: '#06B6D4', height: '100%', transition: 'width 0.3s ease', borderRadius: '4px' }} />
          </div>
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <button
              onClick={handleCancelProcessing}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#999',
                padding: '8px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Show selected file if any */}
      {file && (
        <div className="clipintel-file-selected">
          <span>📁 {file.name}</span>
          <button className="btn-primary btn-sm" onClick={handleUpload} disabled={uploading || processing}>
            {uploading ? (
              <><Loader2 size={14} className="spin" /> Uploading…</>
            ) : processing ? (
              <><Loader2 size={14} className="spin" /> Analyzing...</>
            ) : (
              'Upload & Analyze'
            )}
          </button>
        </div>
      )}

      {/* Save button - only show when there are results */}
      {results && (
        <div className="clipintel-save-row">
          <button
            className="btn-secondary save-btn"
            onClick={() => handleSaveProject()}
            disabled={saveStatus !== 'idle'}
          >
            {saveStatus === 'saving' ? (
              <><Loader2 size={16} className="spin" /><span>Saving...</span></>
            ) : saveStatus === 'saved' ? (
              <><Check size={16} /><span>Saved!</span></>
            ) : (
              <><Save size={16} /><span>Save Project</span></>
            )}
          </button>
        </div>
      )}

      {error && <div className="message error-message">{error}</div>}




      {results?.candidates?.length > 0 && (
        <div className="clipintel-results">
          <div className="results-header">
            <h2>Top clips</h2>
            <div className="clipintel-muted">
              Ranked by score · {results.profile_name} profile · {results.transcript_language}
            </div>
          </div>

          <div className="clip-list">
            {results.candidates
              .filter(c => c.score.recommended || renderedMap.has(c.clip_id))
              .map((c) => (
                <ClipCard
                  key={c.clip_id}
                  clip={c}
                  rendered={renderedMap.get(c.clip_id)}
                  isApproved={approved.has(c.clip_id)}
                  jobId={job.job_id}
                  videoDuration={videoDuration}
                  session={session}
                  onApprove={toggleApprove}
                  onDelete={handleDeleteClip}
                  onRerender={handleRerender}
                  transcript={transcript} // Pass transcript
                  projectData={results} // Pass job as projectData
                  savedCaptionConfig={results.caption_config} // Pass saved caption settings
                />
              ))}
          </div>

          {/* Batch actions */}
          <div className="clipintel-batch-actions">
            <div className="batch-status">
              {approved.size > 0 ? (
                <span className="approved-count">{approved.size} clip{approved.size !== 1 ? 's' : ''} approved</span>
              ) : (
                <span className="clipintel-muted">Approve clips to enable batch download</span>
              )}
            </div>

            <div className="batch-buttons">
              <button
                className="btn-primary"
                onClick={handleDownloadApproved}
                disabled={approved.size === 0 || downloading}
              >
                {downloading ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    Downloading…
                  </>
                ) : (
                  <>
                    <Package size={16} />
                    Download All Approved ({approved.size})
                  </>
                )}
              </button>

              <button className="btn-danger" onClick={handleFinishAndCleanup}>
                <XCircle size={16} />
                Finish & Clean Up
              </button>
            </div>
          </div>
        </div>
      )}

      <CleanupModal
        isOpen={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
        onConfirm={confirmCleanup}
        onSaveAndExit={handleSaveAndExit}
        isSaved={!!projectId}
      />
    </div>
  );
};
